import type { MarketplaceListing, MarketplaceResponse, MarketplaceSort } from '@/types'
import { apiService } from '@/services/api'
import { createIcons, Sparkles } from 'lucide'

export class MarketplaceManager {
  private userId: string
  private currentSort: MarketplaceSort = 'newest'
  private currentPage = 0
  private itemsPerPage = 9
  private totalListings = 0
  private sellerFilter: 'all' | 'mine' = 'all'
  private isLoading = false

  constructor(userId: string) {
    this.userId = userId
    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const sortBtn = target.closest<HTMLButtonElement>('[data-market-sort]')
      if (sortBtn) {
        const sort = sortBtn.dataset.marketSort as MarketplaceSort
        if (sort && sort !== this.currentSort) {
          this.currentSort = sort
          this.currentPage = 0
          this.loadMarketplace(true).catch(console.error)
        }
        return
      }

      const paginationBtn = target.closest<HTMLButtonElement>('#marketplacePrevPage, #marketplaceNextPage')
      if (paginationBtn) {
        if (paginationBtn.id === 'marketplacePrevPage') {
          this.previousPage()
        } else if (paginationBtn.id === 'marketplaceNextPage') {
          this.nextPage()
        }
        return
      }

      const filterBtn = target.closest<HTMLButtonElement>('#marketplaceFilterMine')
      if (filterBtn) {
        this.toggleSellerFilter()
        return
      }

      const buyBtn = target.closest<HTMLButtonElement>('.marketplace-buy-btn')
      if (buyBtn) {
        const listingId = buyBtn.dataset.listingId
        if (listingId) {
          buyBtn.disabled = true
          this.buyListing(listingId, buyBtn).finally(() => {
            buyBtn.disabled = false
          })
        }
        return
      }

      const cancelBtn = target.closest<HTMLButtonElement>('.marketplace-cancel-btn')
      if (cancelBtn) {
        const listingId = cancelBtn.dataset.listingId
        if (listingId) {
          cancelBtn.disabled = true
          this.cancelListing(listingId, cancelBtn).finally(() => {
            cancelBtn.disabled = false
          })
        }
      }
    })

    window.addEventListener('marketplaceListingsChanged', () => {
      // Refresh in-place without resetting pagination to keep context
      this.reloadCurrentPage().catch(console.error)
    })
  }

  async loadMarketplace(resetPage: boolean = false): Promise<void> {
    if (this.isLoading) return
    if (resetPage) {
      this.currentPage = 0
    }

    this.isLoading = true
    this.showLoadingSkeleton()

    try {
      const response = await apiService.getMarketplaceListings(
        this.itemsPerPage,
        this.currentPage * this.itemsPerPage,
        this.currentSort,
        this.sellerFilter === 'mine' ? this.userId : undefined
      )

      this.totalListings = response.meta.total

      this.renderListings(response)
      this.updatePagination()
      this.updateSortButtons()
      this.updateFilterButton()
    } catch (error) {
      this.showError((error as Error).message)
    } finally {
      this.isLoading = false
    }
  }

  async reloadCurrentPage(): Promise<void> {
    if (this.isLoading) return
    await this.loadMarketplace(false)
  }

  private previousPage(): void {
    if (this.currentPage === 0) return
    this.currentPage -= 1
    this.loadMarketplace().catch(console.error)
  }

  private nextPage(): void {
    const maxPage = Math.ceil(this.totalListings / this.itemsPerPage) - 1
    if (this.currentPage >= maxPage) return
    this.currentPage += 1
    this.loadMarketplace().catch(console.error)
  }

  private toggleSellerFilter(): void {
    this.sellerFilter = this.sellerFilter === 'mine' ? 'all' : 'mine'
    this.currentPage = 0
    this.loadMarketplace(true).catch(console.error)
  }

  private updateSortButtons(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-market-sort]').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.marketSort === this.currentSort)
    })
  }

  private updateFilterButton(): void {
    const filterBtn = document.getElementById('marketplaceFilterMine')
    if (!filterBtn) return
    filterBtn.classList.toggle('active', this.sellerFilter === 'mine')
    filterBtn.textContent = this.sellerFilter === 'mine' ? 'Showing My Listings' : 'Show My Listings'
  }

  private updatePagination(): void {
    const prevBtn = document.getElementById('marketplacePrevPage') as HTMLButtonElement | null
    const nextBtn = document.getElementById('marketplaceNextPage') as HTMLButtonElement | null
    const info = document.getElementById('marketplacePageInfo')

    if (prevBtn) {
      prevBtn.disabled = this.currentPage === 0
    }

    const maxPage = Math.ceil(this.totalListings / this.itemsPerPage) - 1
    if (nextBtn) {
      nextBtn.disabled = this.currentPage >= maxPage
    }

    if (info) {
      const start = this.totalListings === 0 ? 0 : this.currentPage * this.itemsPerPage + 1
      const end = Math.min((this.currentPage + 1) * this.itemsPerPage, this.totalListings)
      info.textContent = `${start}-${end} of ${this.totalListings}`
    }
  }

  private showLoadingSkeleton(): void {
    const listEl = document.getElementById('marketplaceList')
    if (!listEl) return

    const skeleton = Array.from({ length: Math.min(this.itemsPerPage, 6) })
      .map(
        () => `
        <div class="fortune-card fortune-card--marketplace skeleton">
          <div class="skeleton-badge"></div>
          <div class="skeleton-line"></div>
          <div class="skeleton-line short"></div>
          <div class="skeleton-footer">
            <div class="skeleton-button"></div>
          </div>
        </div>
      `
      )
      .join('')

    listEl.innerHTML = skeleton
  }

  private renderListings(response: MarketplaceResponse): void {
    const listEl = document.getElementById('marketplaceList')
    if (!listEl) return

    if (response.items.length === 0) {
      listEl.innerHTML = `
        <div class="marketplace-empty">
          <div class="empty-icon">🛍️</div>
          <h3>No listings yet</h3>
          <p>${
            this.sellerFilter === 'mine'
              ? 'You have not listed any fortunes yet. Pick one from your inventory to sell.'
              : 'Check back soon or be the first to list a fortune from your inventory!'
          }</p>
        </div>
      `
      return
    }

    const html = response.items.map((listing) => this.renderListingCard(listing)).join('')
    listEl.innerHTML = html
    
    // Initialize Lucide icons after adding HTML
    createIcons({ icons: { Sparkles } })
  }

  private renderListingCard(listing: MarketplaceListing): string {
    const rarity = listing.rarity_tier?.toLowerCase?.() || 'common'
    const rarityClass = `rarity-${rarity}`
    const rarityBorderClass = `rarity-border-${rarity}`
    const stars = '★'.repeat(listing.stars ?? 0)
    const normalizedSellerId = listing.seller_id != null ? String(listing.seller_id) : ''
    const isOwnListing = normalizedSellerId === this.userId

    const actionButton = isOwnListing
      ? `<button class="marketplace-cancel-btn" data-listing-id="${listing.id}">Cancel Listing</button>`
      : `<button class="marketplace-buy-btn" data-listing-id="${listing.id}">Buy</button>`

    const message = listing.message?.trim() ? listing.message : 'No message'
    const quotedMessage = `&ldquo;${this.escapeHtml(message)}&rdquo;`

    // Display sparkles price if available, fallback to stars for backward compatibility
    const price = listing.price_sparkles 
      ? `${listing.price_sparkles}<i data-lucide="sparkles"></i>`
      : `${listing.price_stars}★`

    return `
      <div class="fortune-card fortune-card--marketplace ${rarityBorderClass}" data-listing-id="${listing.id}">
        <div class="fortune-card-header">
          <span class="rarity-badge ${rarityClass}">
            ${listing.rarity_tier?.toUpperCase?.() || 'FORTUNE'} ${stars}
          </span>
          <span class="price-pill">${price}</span>
        </div>
        <div class="fortune-card-body">
          <p class="fortune-message">${quotedMessage}</p>
        </div>
        <div class="fortune-card-footer fortune-card-footer--marketplace">
          ${actionButton}
        </div>
      </div>
    `
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  private showError(message: string): void {
    const listEl = document.getElementById('marketplaceList')
    if (!listEl) return
    listEl.innerHTML = `
      <div class="marketplace-error">
        <div class="error-icon">⚠️</div>
        <h3>Marketplace unavailable</h3>
        <p>${message}</p>
        <button id="retryMarketplace" class="retry-btn">Try again</button>
      </div>
    `

    const retryBtn = document.getElementById('retryMarketplace')
    retryBtn?.addEventListener('click', () => {
      this.loadMarketplace().catch(console.error)
    })
  }

  private async buyListing(listingId: string, button?: HTMLButtonElement): Promise<void> {
    try {
      button?.classList.add('loading')
      await apiService.purchaseListingWithSparkles(this.userId, listingId)
      window.dispatchEvent(new Event('marketplaceListingsChanged'))
      // Refresh sparkles balance after purchase
      window.dispatchEvent(new Event('sparklesBalanceNeedsRefresh'))
      // Show success message
      const successMsg = document.createElement('div')
      successMsg.className = 'purchase-success-toast'
      successMsg.textContent = 'Fortune purchased successfully!'
      document.body.appendChild(successMsg)
      setTimeout(() => successMsg.remove(), 3000)
    } catch (error) {
      const errorMessage = (error as Error).message.toLowerCase()
      
      if (errorMessage.includes('insufficient') || errorMessage.includes('not enough') || errorMessage.includes('balance')) {
        this.showErrorToast('Insufficient Sparkles to complete the purchase.')
      } else {
        this.showErrorToast(`Unable to purchase: ${(error as Error).message}`)
      }
    } finally {
      button?.classList.remove('loading')
    }
  }


  private showErrorToast(message: string): void {
    const toast = document.createElement('div')
    toast.className = 'error-toast'
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }

  private async cancelListing(listingId: string, button?: HTMLButtonElement): Promise<void> {
    try {
      button?.classList.add('loading')
      await apiService.cancelListing(this.userId, listingId)
      // Refresh the marketplace to show the updated state
      window.dispatchEvent(new Event('marketplaceListingsChanged'))
    } catch (error) {
      alert(`Unable to cancel listing: ${(error as Error).message}`)
    } finally {
      button?.classList.remove('loading')
    }
  }
}
